import { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

/**
 * Toast types with their default configurations
 */
const TOAST_TYPES = {
  success: { duration: 4000, icon: 'check' },
  error: { duration: 6000, icon: 'error' },
  warning: { duration: 5000, icon: 'warning' },
  info: { duration: 4000, icon: 'info' },
};

/**
 * Generate unique ID for toasts
 */
let toastId = 0;
function generateId() {
  return `toast-${++toastId}`;
}

/**
 * ToastProvider - Manages toast state and provides methods to show/dismiss toasts
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  /**
   * Add a new toast
   * @param {string} message - Toast message
   * @param {object} options - Toast options
   * @param {'success'|'error'|'warning'|'info'} options.type - Toast type
   * @param {number} options.duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @param {string} options.title - Optional title
   * @param {string} options.action - Optional action button text
   * @param {function} options.onAction - Optional action button callback
   */
  const addToast = useCallback((message, options = {}) => {
    const {
      type = 'info',
      duration = TOAST_TYPES[type]?.duration || 4000,
      title,
      action,
      onAction,
    } = options;

    const id = generateId();
    const toast = {
      id,
      message,
      type,
      title,
      action,
      onAction,
      createdAt: Date.now(),
    };

    setToasts(prev => [...prev, toast]);

    // Auto-dismiss after duration (unless duration is 0)
    if (duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, duration);
    }

    return id;
  }, []);

  /**
   * Dismiss a toast by ID
   */
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'success' });
  }, [addToast]);

  const error = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'error' });
  }, [addToast]);

  const warning = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'warning' });
  }, [addToast]);

  const info = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'info' });
  }, [addToast]);

  const value = {
    toasts,
    addToast,
    dismissToast,
    dismissAll,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
}

/**
 * Hook to use toast notifications
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastContext;
